import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface MaterialContextType {
  materials: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchMaterials: (page?: number, perPage?: number) => Promise<void>;
  addMaterial: (material: any) => Promise<void>;
  editMaterial: (material: any) => Promise<void>;
  deleteMaterial: (id: number | string) => Promise<void>;
}

export const MaterialContext = createContext<MaterialContextType | undefined>(
  undefined,
);

interface MaterialProviderProps {
  children: ReactNode;
}

export const useMaterialContext = (): MaterialContextType => {
  const context = useContext(MaterialContext);
  if (!context) {
    throw new Error("useMaterialContext must be used within MaterialProvider");
  }
  return context;
};

const MaterialProvider: React.FC<MaterialProviderProps> = ({ children }) => {
  const [materials, setMaterials] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all materials with pagination
  const fetchMaterials = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/materials?${params.toString()}`);
      
      // Handle paginated response structure
      let materialsData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          materialsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          materialsData = responseData.data;
          paginationData = responseData.pagination;
        }
      } else if (Array.isArray(responseData?.data)) {
        materialsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        materialsData = responseData;
      }
      
      setMaterials(materialsData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch materials:", error);
      setMaterials([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new material
  const addMaterial = async (material: any): Promise<void> => {
    try {
      const response = await api.post("/materials", material);
      const newMaterial = response.data?.data || response.data;
      setMaterials((prev) => [...prev, newMaterial]);
    } catch (error) {
      console.error("Failed to add material:", error);
    }
  };

  // Edit an existing material
  const editMaterial = async (material: any): Promise<void> => {
    try {
      const response = await api.put(`/materials/${material.id}`, material);
      const updatedMaterial = response.data?.data || response.data;
      setMaterials((prev) =>
        prev.map((item) => (item.id === material.id ? updatedMaterial : item))
      );
    } catch (error) {
      console.error("Failed to edit material:", error);
    }
  };

  // Delete a material
  const deleteMaterial = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/materials/${id}`);
      setMaterials((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete material:", error);
    }
  };

  useEffect(() => {
    fetchMaterials(1, 15);
  }, [fetchMaterials]);

  return (
    <MaterialContext.Provider
      value={{
        materials,
        pagination,
        loading,
        fetchMaterials,
        addMaterial,
        editMaterial,
        deleteMaterial,
      }}
    >
      {children}
    </MaterialContext.Provider>
  );
};

export default MaterialProvider;
